'use client';

import {
  buildMonitorRequest,
  type MonitorEventKind,
  type MonitorPaginationKey,
} from '@/hooks/integrations/monitor-request';
import { useSession } from '@/providers/session-provider';
import {
  OrganizationConfig,
  type IntegrationManifestService,
  type IntegrationUserAgent,
} from '@pigello/pigello-matrix';
import {
  useQueries,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { DateTime } from 'luxon';
import qs from 'query-string';
import { useCallback, useMemo } from 'react';
import { getServiceCapabilities } from '../service-capabilities';
import { ATTENTION_WINDOW_DAYS } from '../status/derive-status';
import type { MonitorEvent, TaggedMonitorEvent } from '../status/types';

const EVENT_KINDS = [
  'pigello_event',
  'webhook_event',
  'pigello_cron_event',
] as const;

export const INTEGRATION_STATUS_EVENTS_KEY = 'integration-status-events';

export function invalidateIntegrationStatusEvents(
  queryClient: QueryClient,
  agentId?: string
) {
  return queryClient.invalidateQueries({
    queryKey: agentId
      ? [INTEGRATION_STATUS_EVENTS_KEY, agentId]
      : [INTEGRATION_STATUS_EVENTS_KEY],
  });
}

const FAILED_PAGE_LIMIT = 50;
const SUCCEEDED_PAGE_LIMIT = 5;
const STALE_TIME = 5 * 60 * 1000;

// A service that defines no crons can't have cron events — querying the kinds
// the service actually declares cuts the fan-out per agent.
export function monitorEventKindsForService(
  service: IntegrationManifestService | undefined
): MonitorEventKind[] {
  const capabilities = getServiceCapabilities(service);
  const kinds: MonitorEventKind[] = [];
  if (capabilities.events) {
    kinds.push('pigello_event');
  }
  if (capabilities.webhooks) {
    kinds.push('webhook_event');
  }
  if (capabilities.crons) {
    kinds.push('pigello_cron_event');
  }
  return kinds;
}

type MonitorQueryResponse = {
  events: MonitorEvent[];
  next_pagination_key?: MonitorPaginationKey | null;
};

type IntegrationMonitorEvents = {
  eventsByAgentId: Map<string, TaggedMonitorEvent[]>;
  lastSyncByAgentId: Map<string, string>;
  erroredAgentIds: Set<string>;
  pendingAgentIds: Set<string>;
  isLoading: boolean;
};

type QuerySpec = {
  agent: IntegrationUserAgent;
  eventKind: MonitorEventKind;
  kind: 'failed' | 'succeeded';
};

export function useIntegrationMonitorEvents(
  agents: IntegrationUserAgent[],
  opts?: {
    enabled?: boolean;
    eventKindsByAgentId?: Map<string, MonitorEventKind[]>;
    includeLastSync?: boolean;
  }
): IntegrationMonitorEvents {
  const {
    data: {
      session: { token, userId, customerId, organizationId },
    },
  } = useSession();

  const enabled = opts?.enabled ?? true;
  const eventKindsByAgentId = opts?.eventKindsByAgentId;
  const includeLastSync = opts?.includeLastSync ?? true;

  const activeAgents = useMemo(
    () => agents.filter((agent) => agent.isActive !== false),
    [agents]
  );

  const windowStart = DateTime.now()
    .startOf('hour')
    .minus({ days: ATTENTION_WINDOW_DAYS })
    .toUTC()
    .toISO();

  // Single spec list drives both the query array and result attribution, so
  // their ordering can never drift apart.
  const specs = useMemo<QuerySpec[]>(
    () =>
      activeAgents.flatMap((agent) =>
        (eventKindsByAgentId?.get(agent.id) ?? EVENT_KINDS).flatMap(
          (eventKind) =>
            (includeLastSync
              ? (['failed', 'succeeded'] as const)
              : (['failed'] as const)
            ).map((kind) => ({
              agent,
              eventKind,
              kind,
            }))
        )
      ),
    [activeAgents, eventKindsByAgentId, includeLastSync]
  );

  const combineResults = useCallback(
    (
      results: UseQueryResult<MonitorQueryResponse, Error>[]
    ): IntegrationMonitorEvents => {
      const eventsByAgentId = new Map<string, TaggedMonitorEvent[]>();
      const lastSyncByAgentId = new Map<string, string>();
      const erroredAgentIds = new Set<string>();
      const pendingAgentIds = new Set<string>();
      let isLoading = false;

      results.forEach((result, index) => {
        const spec = specs[index];
        if (!spec) return;
        const agentId = spec.agent.id;
        if (result.isPending) {
          if (enabled) {
            isLoading = true;
            pendingAgentIds.add(agentId);
          }
          return;
        }

        if (result.isError) {
          erroredAgentIds.add(agentId);
          return;
        }

        const events = result.data?.events ?? [];

        if (spec.kind === 'succeeded') {
          for (const evt of events) {
            if (!evt.succeeded_at) continue;
            const syncedAt = evt.succeeded_at;
            const current = lastSyncByAgentId.get(agentId);
            if (
              !current ||
              DateTime.fromISO(syncedAt) > DateTime.fromISO(current)
            ) {
              lastSyncByAgentId.set(agentId, syncedAt);
            }
          }
          return;
        }

        const existing = eventsByAgentId.get(agentId) ?? [];
        eventsByAgentId.set(agentId, [
          ...existing,
          ...events.map((evt) => ({
            ...evt,
            monitorEventKind: spec.eventKind,
          })),
        ]);
      });

      return {
        eventsByAgentId,
        lastSyncByAgentId,
        erroredAgentIds,
        pendingAgentIds,
        isLoading,
      };
    },
    [enabled, specs]
  );

  return useQueries({
    queries: specs.map(({ agent, eventKind, kind }) => {
      const { url, headers } = buildMonitorRequest({
        eventKind,
        applicationId: agent.applicationId,
        serviceId: agent.serviceId,
        serviceVersionId: agent.serviceVersionId,
        targetContentType:
          agent.activationContentType?.id || OrganizationConfig.contentType,
        targetObjectId: agent.activationObjectId || organizationId,
        session: { token, userId, customerId },
        pageLimit: kind === 'failed' ? FAILED_PAGE_LIMIT : SUCCEEDED_PAGE_LIMIT,
        queryString: qs.stringify(
          kind === 'failed'
            ? { has_failed: true, created_at_start: windowStart }
            : { has_succeeded: true, created_at_start: windowStart },
          { skipEmptyString: true, skipNull: true }
        ),
      });

      return {
        queryKey: [
          INTEGRATION_STATUS_EVENTS_KEY,
          agent.id,
          kind,
          eventKind,
          windowStart,
        ],
        queryFn: async (): Promise<MonitorQueryResponse> => {
          const res = await fetch(url, { headers });
          if (!res.ok) {
            throw new Error(`Monitor query failed: ${res.status}`);
          }
          return res.json();
        },
        staleTime: STALE_TIME,
        gcTime: 30 * 60 * 1000,
        retry: 1,
        enabled,
      };
    }),
    combine: combineResults,
  });
}
